/**
 * Kiosk Wristband Addon Transfer API Contract Schemas
 *
 * Kiosk-specific aliases for the ticketing addon-transfer schemas plus a
 * kiosk-side request schema that omits `senderWristbandCode` (it comes from
 * the URL path) and enforces `ticketIds.min(1)` (ticketing's schema lacks it).
 *
 * All symbols are prefixed with `Kiosk` to avoid star-export collisions at the
 * root barrel (`packages/api-contracts/src/index.ts` does `export *` from both
 * `./ticketing` and `./kiosk`).
 *
 * Deep-import from the leaf, not the barrel, to avoid circular deps — same
 * rule as kiosk/wristband-identity.ts imports from ticketing/wristband.ts.
 */

import { z } from 'zod';
import {
  TransferableAddonSchema,
  TransferableAddonsResponseSchema,
  CreateAddonTransferResponseSchema,
} from '../ticketing/addon-transfers';

// ============================================================================
// Aliases (Kiosk-prefix to avoid root-barrel star-export collisions)
// ============================================================================

export const KioskTransferableAddonSchema = TransferableAddonSchema;
export type KioskTransferableAddon = z.infer<typeof KioskTransferableAddonSchema>;

export const KioskTransferableAddonsResponseSchema = TransferableAddonsResponseSchema;
export type KioskTransferableAddonsResponse = z.infer<typeof KioskTransferableAddonsResponseSchema>;

export const KioskCreateAddonTransferResponseSchema = CreateAddonTransferResponseSchema;
export type KioskCreateAddonTransferResponse = z.infer<
  typeof KioskCreateAddonTransferResponseSchema
>;

// ============================================================================
// Kiosk-specific request schema
// ============================================================================

export const KioskCreateAddonTransferRequestSchema = z.object({
  idempotencyKey: z.string().min(1).max(64),
  recipientWristbandCode: z.string().trim().min(1),
  ticketIds: z.array(z.string().uuid()).min(1),
});
export type KioskCreateAddonTransferRequest = z.infer<
  typeof KioskCreateAddonTransferRequestSchema
>;

// ============================================================================
// Error vocabulary
// ============================================================================

export const KIOSK_ADDON_TRANSFER_ERROR = {
  INVALID_SENDER: 'TRANSFER_INVALID_SENDER',
  INVALID_RECIPIENT: 'TRANSFER_INVALID_RECIPIENT',
  SELF: 'TRANSFER_SELF',
  DIFFERENT_ORDER: 'TRANSFER_DIFFERENT_ORDER',
  RECIPIENT_NOT_FOUND: 'TRANSFER_RECIPIENT_NOT_FOUND',
  NO_CUSTOMER: 'TRANSFER_NO_CUSTOMER',
  TICKETS_UNAVAILABLE: 'TRANSFER_TICKETS_UNAVAILABLE',
  UPSTREAM: 'TRANSFER_UPSTREAM_ERROR',
} as const;

export type KioskAddonTransferErrorCode =
  (typeof KIOSK_ADDON_TRANSFER_ERROR)[keyof typeof KIOSK_ADDON_TRANSFER_ERROR];
